/**
 * Real workspace/account/impersonation data + handlers for the new
 * AppShell's Sidebar, reusing the same real data sources and action
 * sequences already used by PageTemplate/UserMenu/* and
 * PageTemplate/PageHeader.js (the old top bar) — not re-derived from
 * scratch. Owned alongside useSidebarNavData/useSidebarAlerts so all
 * "real data sourcing for the new shell" logic lives in one place, easy to
 * find/delete alongside PageTemplate once the old shell is retired.
 */
import { useHistory } from "react-router-dom";
import { useUserStore } from "../../../../hooks/redux/user";
import { useCurrentOrganizationStore } from "../../../../hooks/redux/currentOrganization";
import { useAutologinStore } from "../../../../hooks/redux/autologin";
import { useAppStore } from "../../../../hooks/redux/app";
import appConfig from "../../../../appConfig";
import { getUploadResourceUrl } from "../../../../helpers/url";
import AccountMenu from "../AppShell/AccountMenu/AccountMenu";

// No existing "get initials" helper in the codebase (only OrgLogo.js's
// single-letter organization.name.at(0) fallback) — first letter of the
// first two words, uppercased.
export const initialsFromName = (name) => {
  if (!name) return "?";
  const words = name.trim().split(/\s+/);
  return words
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
};

export function useAccountShellData() {
  const { user, changeOrganization, logout } = useUserStore();
  const { currentOrganization } = useCurrentOrganizationStore();
  const { autologin, autologout } = useAutologinStore();
  const { app, toggleAdminNavigation, shutdownIntercom } = useAppStore();
  const history = useHistory();

  const userModel = user.model || {};
  const orgModel = currentOrganization.model || {};

  const workspaces = (userModel.organizations || []).map((org) => ({
    uuid: org.uuid,
    name: org.name,
    initials: initialsFromName(org.name),
    // Same source as the old UserMenu/OrgLogo.js: an uploaded favicon
    // beats the initials fallback whenever the workspace has one set.
    icon: org.settings?.favicon
      ? getUploadResourceUrl(org.settings.favicon)
      : null,
    current: org.uuid === userModel.current_org_uuid,
  }));
  const currentWorkspace = workspaces.find((w) => w.current);

  const onSwitchWorkspace = (uuid) => {
    // Same guard OrgSelector.js applies before calling changeOrganization.
    if (uuid === userModel.current_org_uuid) return;
    changeOrganization(uuid);
  };

  const account = {
    name: userModel.name,
    email: userModel.email,
    initials: initialsFromName(userModel.name),
  };

  const impersonating = Boolean(autologin.model);

  const impersonation = {
    active: impersonating || Boolean(autologin.login),
    // The workspace being viewed, not the impersonator's own name — the new
    // ImpersonationBar's copy ("Viewing {account} as an administrator") and
    // AccountRow's "Viewing as" eyebrow are both account/workspace-scoped,
    // a deliberate wording change from the old top bar's person-scoped
    // "Auto login as: {name}".
    accountName: impersonating ? orgModel.name : undefined,
    onStop: async () => {
      await autologout(autologin);
      history.push("/dashboard");
    },
  };

  const onLogout = async () => {
    // Same order/timing as UserMenu/DropdownFooter.js's onLogoutClick.
    shutdownIntercom();
    await logout();
    setTimeout(() => history.push("/login"), 200);
  };

  const renderAccountMenu = (close) => (
    <AccountMenu
      isAdmin={userModel.role === "admins"}
      adminNavOpen={app.showAdminNav}
      onToggleAdminNav={() => toggleAdminNavigation()}
      helpUrl={`https://help.${appConfig.domain}/en/`}
      accountSettingsHref="/settings"
      affiliatesHref="/settings/affiliates"
      onLogout={onLogout}
      onClose={close}
    />
  );

  return {
    workspaces,
    currentWorkspace,
    onSwitchWorkspace,
    account,
    impersonating,
    impersonation,
    renderAccountMenu,
  };
}
